export type MegaLink = { label: string; href: string };

export type MegaColumn = {
  /** Never shown on the site — it is the key the admin navigation editor
   *  (NavMenuItem.column) uses to take a column over. Renaming one orphans
   *  every CMS row saved against the old title, so treat it as an id. */
  title: string;
  links: MegaLink[];
};

export type MegaMenu = {
  key: string;
  label: string;
  href: string;
  /** Small tag after the label, e.g. "Soon" on eShop. */
  badge?: string;
  /** Empty = a plain link with no dropdown (Resorts, Tours). */
  columns: MegaColumn[];
};

/**
 * Primary navigation, 2026-09 simplification.
 *
 * Five items, dropdowns only where there is something to choose between.
 * Resorts and Tours are direct links: their pages are the finders, so an
 * "All resorts" dropdown would only repeat the top-level link.
 *
 * Column titles deliberately differ from every pre-2026-09 title ("Regions",
 * "By Interest", "Read"…) so the CMS rows saved against the old menu cannot
 * override this one. See /admin/navigation → "Retired menu links".
 *
 * Corporate pages (Invest With Us, Partners, Careers, Education & Training,
 * Policy Initiatives) live in the footer's Corporate column, not here.
 *
 * Home leads the menu as a plain link (owner, 1 Oct 2026: "add home button").
 */
export const MEGA_MENUS: MegaMenu[] = [
  { key: "home", label: "Home", href: "/", columns: [] },
  {
    key: "destinations",
    label: "Destinations",
    href: "/destinations",
    columns: [
      {
        title: "Destinations",
        links: [
          { label: "Explore by Region", href: "/#regions" },
          { label: "All Places", href: "/places" },
          { label: "Area Guides", href: "/areas" },
          { label: "Interactive Map", href: "/maps" },
          { label: "Road Trips", href: "/routes" },
          // /trails was reachable only from a trail's breadcrumb or a map pin
          // before it had a menu entry — keep it in the header.
          { label: "Trails", href: "/trails" },
        ],
      },
    ],
  },
  {
    key: "experiences",
    label: "Experiences",
    href: "/experiences",
    columns: [
      {
        title: "Experiences",
        links: [
          // The six with the most content (published places and tours in the
          // CMS, 2026-09). Every other category, and the event calendar, is
          // in the footer's Experiences column — keep both lists in step.
          { label: "All Experiences", href: "/experiences" },
          { label: "Mountaineering & Treks", href: "/experiences/mountaineering" },
          { label: "Eco & Nature", href: "/experiences/eco-nature" },
          { label: "Cultural Heritage", href: "/experiences/cultural-heritage" },
          { label: "Faith & Pilgrimage", href: "/experiences/religious" },
          { label: "Adventure", href: "/experiences/adventure" },
        ],
      },
    ],
  },
  {
    key: "stays",
    label: "Resorts",
    href: "/stays",
    columns: [],
  },
  {
    key: "tours",
    label: "Tours",
    href: "/tours",
    // By kind of trip (owner, 5 Oct 2026), so a visitor lands on the trips
    // they came for; keys match design-data/tour-kinds.ts. Editable in
    // Admin → Site Navigation like the other columns.
    columns: [
      {
        // A title no pre-October row ever used, so no old CMS row can take it over.
        title: "Kinds of Trip",
        links: [
          { label: "All Tours", href: "/tours" },
          { label: "Weekend & Day Trips", href: "/tours?kind=weekend#kind-weekend" },
          { label: "Hunza & Skardu", href: "/tours?kind=north#kind-north" },
          { label: "Treks & Expeditions", href: "/tours?kind=treks#kind-treks" },
          { label: "Culture & Heritage", href: "/tours?kind=culture#kind-culture" },
          { label: "Festivals & Seasonal", href: "/tours?kind=seasonal#kind-seasonal" },
          { label: "Lakes, Valleys & Coast", href: "/tours?kind=valleys#kind-valleys" },
        ],
      },
      {
        // Verified local guides (Tours department, 30 Oct 2026). A new title,
        // so no CMS row saved against another column can take it over.
        title: "Local Guides",
        links: [
          { label: "Find a Guide", href: "/guides" },
          { label: "Become a Guide", href: "/education-training/tour-guide-enlistment" },
        ],
      },
    ],
  },
  // eShop, announced Nov 2026 (owner): a plain link to the coming-soon
  // page until the shop opens; drop the badge then.
  { key: "shop", label: "eShop", href: "/shop", badge: "Soon", columns: [] },
  {
    key: "about",
    label: "About",
    href: "/about",
    columns: [
      {
        title: "About",
        links: [
          { label: "Our Story", href: "/about" },
          { label: "Journal", href: "/journal" },
          { label: "Travel Essentials", href: "/how-to-travel" },
          { label: "How Booking Works", href: "/how-to-book" },
          { label: "Contact", href: "/contact" },
        ],
      },
    ],
  },
];

/**
 * CMS rows saved against a menu or column that is no longer in `menus`. The
 * site ignores them (overrides match by menu key + column title), so the admin
 * editor lists them separately, read-only, rather than leaving them invisible.
 */
export function retiredNavRows<T extends { menuKey: string; column: string }>(
  rows: T[],
  menus: MegaMenu[] = MEGA_MENUS,
): T[] {
  const known = new Set(menus.flatMap((m) => m.columns.map((c) => `${m.key}\u0000${c.title}`)));
  return rows.filter((r) => !known.has(`${r.menuKey}\u0000${r.column}`));
}

/**
 * Paths that belong to a menu but are not in its dropdown, so the active
 * indicator still lights up there. /regions only redirects to /destinations;
 * /mountains is a destination guide linked from the homepage and footer;
 * the event calendar sits in the footer's Experiences column.
 */
const EXTRA_SECTION_PATHS: Record<string, string[]> = {
  destinations: ["/regions", "/mountains"],
  experiences: ["/events"],
};

function pathOnly(href: string): string {
  return href.split(/[?#]/)[0] || "/";
}

/** True when `pathname` is `base` itself or anything nested under it. */
function isUnder(pathname: string, base: string): boolean {
  return pathname === base || pathname.startsWith(`${base}/`);
}

/** The key of the menu whose section contains `pathname`, or null. */
export function activeMenuKey(menus: MegaMenu[], pathname: string): string | null {
  // "/" is filtered out of every section's bases below (it would match every
  // page), so the Home item is matched on the home page alone.
  if (pathname === "/" && menus.some((m) => m.key === "home")) return "home";
  for (const menu of menus) {
    const bases = [
      menu.href,
      ...menu.columns.flatMap((c) => c.links.map((l) => l.href)),
      ...(EXTRA_SECTION_PATHS[menu.key] ?? []),
    ]
      .filter((href) => href.startsWith("/"))
      .map(pathOnly)
      .filter((p) => p !== "/");
    if (bases.some((base) => isUnder(pathname, base))) return menu.key;
  }
  return null;
}
